import React, { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import Checkbox from "../../../../../../../../components/Form/Checkbox";
import Modal from "../../../../../../../../components/Generic/Modal";
import SettingsUserForm from "../../../../../../../../components/Generic/SettingsUserForm";
import { useAppState } from "../../../../../../../../context/StateContext";
import { AutomaticSequencing } from "../../../../../../../../models/automatic.sequencing";
import { Contact } from "../../../../../../../../models/contact";
import { Timezone } from "../../../../../../../../models/timezone";
import { User } from "../../../../../../../../models/user";
import TimezoneService from "../../../../../../../../services/timezone.service";
import { CompanyProfile, Status, UserTypes } from "../../../../../../../../util/enums";
import { userScheme } from "../../../../../../../../util/genericSchemas";
import useStyles from "./styles";

interface Props {
  sequence?: AutomaticSequencing;
  selectedContact?: Contact;
  open: boolean;
  isLoading: boolean;
  handleClose: () => void;
  handleUpdateOrCreateContact: (contact: Contact) => void;
}

const ContactModal = ({
  sequence,
  selectedContact,
  open,
  isLoading,
  handleClose,
  handleUpdateOrCreateContact,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [contact, setContact] = useState<Contact>();

  const [isRegisteringAsUser, setIsRegisteringAsUser] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timezones, setTimezones] = useState<Timezone[]>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const getUserCode = useCallback(
    (sequence?: AutomaticSequencing): string => {
      return `${sequence?.prefix ? sequence?.prefix : ""}${
        sequence?.nextValue ? sequence?.nextValue : ""
      }`;
    },
    [sequence]
  );

  useQuery(
    ["timezones.list"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) {
          setTimezones(response.data);
        }
      },
    }
  );

  const { genericObject } = userScheme(translate, contact?.user, currentUser);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    lastName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    phone: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    email: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup.string().when("isProfileMoving", {
      is: true,
      then: yup.string().test({
        name: "validate-code",
        message: translate("thisFieldIsRequired"),
        test: (value) => {
          if (!value && contact?.user?.typeUser === UserTypes.SALES) return false;

          return true;
        },
      }),
    }),
    position: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    isUser: yup.boolean(),
    username: yup.string().when("isUser", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    typeUser: yup.string().when("isUser", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    roleId: yup.number().when("isUser", {
      is: true,
      then: yup.number().required(translate("thisFieldIsRequired")),
    }),
    status: yup.string().when("isUser", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    timezoneId: yup.number().when("isUser", {
      is: true,
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
  });

  const handleUpdateContactObject = (user: User) => {
    const { name, lastName, email, phone, position } = user;
    const defaultContact = {
      firstName: name,
      lastName,
      email,
      mobilePhone: phone || "",
      position,
      user: { ...user },
    };

    setContact((state) =>
      state
        ? {
            ...state,
            ...defaultContact,
          }
        : defaultContact
    );
  };

  const handleValidationSuccess = (contact?: Contact): boolean => {
    schema.validateSync(
      {
        ...genericObject(contact?.user),
        name: contact?.firstName,
        lastName: contact?.lastName,
        phone: contact?.mobilePhone,
        email: contact?.email,
        position: contact?.position,
        isUser: isRegisteringAsUser,
        isProfileMoving:
          currentUser?.company?.profile &&
          currentUser?.company?.profile?.initials === CompanyProfile.MV &&
          isRegisteringAsUser,
        timezoneId: contact?.user?.timezoneId,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidationError = (err: any): boolean => {
    if (err instanceof yup.ValidationError) {
      setErrors(err?.inner);
    }

    return false;
  };

  const handleValidateForm = (contact?: Contact): boolean => {
    try {
      return handleValidationSuccess(contact);
    } catch (error) {
      return handleValidationError(error);
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (handleValidateForm(contact)) {
      const contactToSubmit = {
        ...contact,
        companyId: currentUser?.companyId,
        user:
          isRegisteringAsUser || contact?.userId
            ? {
                ...contact?.user,
                companyId: currentUser?.companyId,
              }
            : undefined,
      } as Contact;

      handleUpdateOrCreateContact(contactToSubmit);
    }
  };

  const setTimezoneValue = useCallback(() => {
    const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const timezoneFound = timezones?.find((timezone) => timezone.name === currentTimezone);

    return timezoneFound?.id;
  }, [timezones]);

  const getButtonSubmitText = () => {
    return selectedContact
      ? selectedContact.userId
        ? translate("editContactUser")
        : translate("editContact")
      : isRegisteringAsUser
      ? translate("addContactUser")
      : translate("addContact");
  };

  useEffect(() => {
    if (isSubmitted && contact) handleValidateForm(contact);
  }, [isSubmitted, contact]);

  useEffect(() => {
    if (selectedContact) {
      if (selectedContact.userId) setIsRegisteringAsUser(true);
      let mobilePhone: string = selectedContact?.mobilePhone;

      if (
        !selectedContact?.mobilePhone?.includes("+") &&
        !isValidPhoneNumber(selectedContact?.mobilePhone) &&
        selectedContact?.mobilePhone?.length > 0
      )
        mobilePhone = `+${selectedContact?.mobilePhone}`;
      if (selectedContact.user) {
        setContact({
          ...selectedContact,
          id: selectedContact.id,
          mobilePhone,
          user: {
            ...selectedContact.user,
            position: selectedContact.position,
            phone: mobilePhone,
          },
        });
      } else {
        setContact({
          ...selectedContact,
          id: selectedContact.id,
          user: {
            name: selectedContact.firstName,
            lastName: selectedContact.lastName,
            email: selectedContact.email,
            status: Status.ACTIVE,
            typeUser: UserTypes.USER,
            code: getUserCode(sequence),
            timezoneId: setTimezoneValue(),
            phone: mobilePhone,
            position: selectedContact.position,
          } as User,
        });
      }
    }
  }, [selectedContact, timezones]);

  useEffect(() => {
    if (isRegisteringAsUser && !contact?.id) {
      setContact({
        ...contact,
        user: {
          name: contact?.firstName,
          lastName: contact?.lastName,
          email: contact?.email,
          phone: contact?.mobilePhone,
          position: contact?.position,
          status: Status.ACTIVE,
          typeUser: UserTypes.USER,
          code: getUserCode(sequence),
          timezoneId: setTimezoneValue(),
        },
      } as Contact);
    }
  }, [isRegisteringAsUser, sequence, timezones]);

  return (
    <Modal
      data-cy="businessContactsModal"
      open={open}
      handleClose={handleClose}
      showActionButtons
      actionButtonProps={{
        dataCySave: "saveContactButton",
        dataCyCancel: "cancelButton",
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: getButtonSubmitText(),
        onSave: handleSubmit,
        onCancel: () => {
          handleClose();
        },
      }}
      title={selectedContact ? translate("editContact") : translate("addNewContact")}
    >
      <div data-cy="addBusinessContactModal">
        <section className={classes.modalSection}>
          <SettingsUserForm
            updateOrCreate={(user: User) => handleUpdateContactObject(user)}
            userInformation={contact?.user}
            showingFromPeopleSettings={false}
            isRegisteringUser={isRegisteringAsUser}
            errors={errors}
          />
          <div className="mt-3">
            <Checkbox
              data-cy="registerAsUserCheckbox"
              label={translate("registerAsAnUser")}
              checked={isRegisteringAsUser}
              onChange={(_, checked) => setIsRegisteringAsUser(checked)}
            />
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ContactModal;
